import { useCallback, useEffect, useState } from 'react';
import { Route, Routes } from 'react-router-dom';
import type { ProjectDocument } from 'common/project/ProjectDocument';
import { validateProject } from 'common/project/validateProject';
import { EditAppContext, type EditAppState, useEditApp } from 'client/lib/EditApp';
import { useApi } from 'client/lib/trpc';
import { useProjectState } from 'client/state/ProjectState';
import { ProjectDomainDialog } from 'client/pages/project/components/ProjectDomainDialog';
import { Dashboard } from 'client/components/Layout/Dashboard';
import { ProjectDeploymentsTab } from './ProjectDeploymentsTab';
import { ProjectOptionsTab } from './ProjectOptionsTab';
import { ProjectSubheader } from './ProjectSubheader';

export const ProjectPage = ({ path = null }: { path?: 'options' | null }) => {
  const api = useApi();
  const [isDomainChangeOk, setIsDomainChangeOk] = useState(false);
  const [isDomainOpen, setIsDomainOpen] = useState(false);

  const handleSaveParams = useCallback(
    (state: EditAppState<ProjectDocument>) =>
      state.document && {
        document: {
          id: state.document.id,
          name: state.document.name,
          tweakedDomainSlug: state.document.tweakedDomainSlug,
          componentsWithValues: state.document.componentsWithValues,
        },
      },
    []
  );

  const handleBeforeSave = useCallback(
    (currentDocument: ProjectDocument, originalDocument: ProjectDocument) => {
      if (currentDocument.tweakedDomainSlug !== originalDocument.tweakedDomainSlug && !isDomainChangeOk) {
        setIsDomainOpen(true);
        return false;
      }
      return true;
    },
    [isDomainChangeOk]
  );

  const handleSave = useCallback(() => setIsDomainChangeOk(false), []);

  const editAppContext = useEditApp<ProjectDocument>({
    idKey: 'projectID',
    loadUrl: api.project.load,
    // @ts-expect-error useEditApp does not support tRPC properly
    saveUrl: api.project.save,
    store: useProjectState,
    getSaveParams: handleSaveParams,
    validator: validateProject,
    onBeforeSave: handleBeforeSave,
    onSave: handleSave,
  });

  const handleChangeConfirmation = () => {
    setIsDomainChangeOk(true);
    setIsDomainOpen(false);
  };

  // Trigger a new save after change confirmation
  useEffect(() => {
    if (isDomainChangeOk) editAppContext.save();
  }, [isDomainChangeOk]);

  const handleClose = () => setIsDomainOpen(false);

  return (
    <EditAppContext.Provider value={editAppContext}>
      <ProjectSubheader />
      <Dashboard>
        {path === null && <ProjectDeploymentsTab />}
        {path === 'options' && <ProjectOptionsTab />}
      </Dashboard>

      <ProjectDomainDialog open={isDomainOpen} onSave={handleChangeConfirmation} onClose={handleClose} />
    </EditAppContext.Provider>
  );
};
